/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Loader } from '../../atoms/loader';
import { getPlanPrice, getPlanDuration, getGroupsData, isPbPreview } from '../../layout-utils';
import { Attribute } from '../../attribute';
import { SectionHeader } from '../loginless-common/section-header';
import { LoginlessLayout } from '../loginless-common/loginless-layout';
import './loginless-flat.m.css';

const PlanList = ({ plans, selectedPlan, onSelect, subscriptionConfig, mode, localizedTime }) => {
  const { planSelectionHeading = 'Select Your Plan', showOfferPrice = false } = subscriptionConfig;
  const isSinglePlan = plans.length === 1;
  const planGridStyle = `plan-grid ${isSinglePlan ? 'single-plan' : ''}`.trim();

  return (
    <div styleName="plan-selection-container">
      <SectionHeader title={planSelectionHeading || "Select Your Plan"} mode={mode} />
      <div styleName={planGridStyle}>
        {plans.map((plan) => {
          const isSelected = selectedPlan?.id === plan.id;
          const planCardStyle = `plan-card ${isSinglePlan ? 'single-card' : ''} ${isSelected ? 'selected' : ''} ${mode === 'dark' ? 'dark' : ''}`.trim();
          const radioCircleStyle = `radio-circle ${isSelected ? 'selected' : ''}`.trim();
          const price = getPlanPrice(plan.price_currency, plan.price_cents);
          const duration = getPlanDuration(localizedTime, plan);

          const hasDiscountAttrs = plan.custom_attributes?.some(
            (a) => (a.name === 'Actual Price' || a.name === 'Discount') && a.value
          );

          return (
            <div
              key={plan.id}
              styleName={planCardStyle}
              onClick={() => onSelect(plan)}
              data-test-id={`loginless-subscription-single-plan-${plan.id}`}
              role="button"
              tabIndex={0}
            >
              {/* Radio button + Tag header */}
              <div styleName="card-top-row">
                <div styleName={radioCircleStyle} data-test-id="subscription-plan-card-radio">
                  {isSelected && <div styleName="radio-inner" />}
                </div>
                <Attribute data={plan} attributeName="Tag" />
              </div>

              {/* Title */}
              <h3 styleName="plan-title">{plan.title}</h3>

              {/* Price & Duration */}
              <div styleName="price-row">
                <span styleName="price-amount">{price}</span>
                <span styleName="price-duration">/ {duration}</span>
              </div>

              {/* Strikethrough price & Discount pill */}
              {showOfferPrice && (
                hasDiscountAttrs ? (
                  <div styleName="discount-row">
                    <Attribute data={plan} attributeName="Actual Price" />
                    <Attribute data={plan} attributeName="Discount" />
                  </div>
                ) : (
                  <div styleName="discount-row placeholder" aria-hidden="true" />
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const LoginlessFlat = (props) => {
  const subscriptionConfig = get(props, ['config', 'pagebuilder-config', 'subscription', 'pageSetting'], {});
  const isPB = isPbPreview(props);

  const groups = getGroupsData(props.currentPath, props.subscriptions) || [];
  const group = groups[0] || {};
  const plans = group.subscription_plans || [];
  const [selectedPlan, setSelectedPlan] = useState(plans[0] || null);
  const activePlan = selectedPlan || plans[0] || null;

  useEffect(() => {
    if (!selectedPlan && plans.length > 0) {
      setSelectedPlan(plans[0] || null);
    }
  }, [plans]);

  if (props?.isAccessTypeUserSetLoading) {
    return <Loader data={plans} subscriptionConfig={subscriptionConfig} />;
  }

  const enableDarkMode = get(props, ['enableDarkMode'], false);
  const mode = enableDarkMode ? 'dark' : 'light';

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const activeGroup =
    groups.find(
      (g) => g.id === activePlan?.subscription_group_id || g.subscription_plans?.some((p) => p.id === activePlan?.id)
    ) || group || {};

  const dynamicDescription = activePlan?.description || activeGroup?.description;

  return (
    <LoginlessLayout
      subscriptionConfig={subscriptionConfig}
      mode={mode}
      isPB={isPB}
      activePlan={activePlan}
      description={dynamicDescription}
      descriptionType="plan"
      config={props?.config}
      localizedTime={props?.localizedTime}
      initLoginlessSubscription={props?.initLoginlessSubscription}
      navigateTo={props?.navigateTo}
      member={props?.member}
    >
      <PlanList
        plans={plans}
        selectedPlan={activePlan}
        onSelect={handleSelectPlan}
        subscriptionConfig={subscriptionConfig}
        mode={mode}
        localizedTime={props?.localizedTime}
      />
    </LoginlessLayout>
  );
};

LoginlessFlat.propTypes = {
  subscriptions: PropTypes.array,
  currentPath: PropTypes.string,
  config: PropTypes.object,
  isPbPreview: PropTypes.bool,
  defaultSubscriptionHandler: PropTypes.func,
  isAccessTypeUserSetLoading: PropTypes.bool,
  enableDarkMode: PropTypes.bool,
  localizedTime: PropTypes.string,
  initLoginlessSubscription: PropTypes.func,
  navigateTo: PropTypes.func,
  member: PropTypes.object
};
